/**
 * Parses the `feeMeta` object the gateway worker (worker.js) attaches to
 * KyberSwap quote/build responses. The worker is the single source of truth
 * for fee configuration (FEE_BPS / FEE_RECEIVER / chargeFeeBy) — this parser
 * lets the client trust that config instead of keeping its own hardcoded
 * copy that can silently drift out of sync.
 *
 * Deliberately zod-free: this app does not depend on zod (checked
 * package.json), so this is a small manual structural validator instead of
 * pulling in a new dependency for one shape.
 *
 * Returns `undefined` on anything malformed. Resolution callers distinguish
 * an absent field (legacy worker, safe fallback) from a present field that
 * failed this parser (invalid response, fail closed).
 */

// The token the app fee is deducted in. Mirrors worker.js's kyberFeeMeta():
// 'currency_in' for native-token input (fee removed from amountIn pre-swap),
// 'currency_out' for ERC-20 input (fee removed from amountOut post-swap).
export type ChargeFeeBy = 'currency_in' | 'currency_out'

export interface KyberFeeMeta {
  feeBps: bigint
  feeRecipient: string
  quoteIsNetOfFee: boolean
  chargeFeeBy: ChargeFeeBy
}

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isChargeFeeBy(value: unknown): value is ChargeFeeBy {
  return value === 'currency_in' || value === 'currency_out'
}

export function parseKyberFeeMeta(raw: unknown): KyberFeeMeta | undefined {
  if (!isPlainObject(raw)) {
    return undefined
  }

  const { feeBps, feeRecipient, quoteIsNetOfFee, chargeFeeBy } = raw

  if (typeof feeBps !== 'number' || !Number.isFinite(feeBps) || !Number.isInteger(feeBps)) {
    return undefined
  }
  // Reject >= 10_000 (100%), not just > 10_000: the output-fee grossing math divides by
  // (10_000 - feeBps) (state/routing/utils.ts), so exactly 10_000 bps is a division by zero during
  // trade construction. A 100% fee is nonsensical anyway.
  if (feeBps < 0 || feeBps >= 10_000) {
    return undefined
  }

  if (typeof feeRecipient !== 'string' || !ADDRESS_RE.test(feeRecipient)) {
    return undefined
  }

  if (typeof quoteIsNetOfFee !== 'boolean') {
    return undefined
  }

  // Reject anything other than the two values worker.js actually emits -
  // an unrecognized chargeFeeBy means we can't safely know which token the
  // fee was denominated in, so reject the whole feeMeta rather than guessing.
  // The resolver will fail closed because the field was present but invalid.
  if (!isChargeFeeBy(chargeFeeBy)) {
    return undefined
  }

  return {
    feeBps: BigInt(feeBps),
    feeRecipient,
    quoteIsNetOfFee,
    chargeFeeBy,
  }
}

/**
 * True when two feeMeta values describe the same fee contract. Used to
 * detect the case where the quote-time feeMeta (what execution needs to
 * stay consistent with) and the build-time feeMeta (fetched fresh at swap
 * time) disagree — e.g. because the worker's fee config changed between the
 * two requests. This is a transaction-safety check only; it does not govern
 * what the UI displays (see routing/utils.ts, which binds the displayed fee
 * to the same feeMeta independently). Recipient comparison is
 * case-insensitive (addresses); chargeFeeBy must match exactly since it
 * determines which token the fee is denominated in.
 */
export function kyberFeeMetaAgrees(a: KyberFeeMeta, b: KyberFeeMeta): boolean {
  return (
    a.feeBps === b.feeBps &&
    a.quoteIsNetOfFee === b.quoteIsNetOfFee &&
    a.chargeFeeBy === b.chargeFeeBy &&
    a.feeRecipient.toLowerCase() === b.feeRecipient.toLowerCase()
  )
}

export interface ResolvedKyberFeeMeta {
  feeBps: bigint
  quoteIsNetOfFee: boolean
  chargeFeeBy: ChargeFeeBy
}

export interface KyberFeeResolution {
  fee: ResolvedKyberFeeMeta
  /** Present when either response supplied feeMeta but its value failed strict parsing. */
  invalid?: { quote: boolean; build: boolean }
  /** Present only when quote-time and build-time feeMeta were both valid but disagreed. */
  disagreement?: { quote: KyberFeeMeta; build: KyberFeeMeta }
}

/**
 * The single fee-resolution policy used by useUniversalRouterSwapCallback:
 *  1. If either response contains present-but-invalid feeMeta, surface an
 *     invalid result so execution fails closed. Only a genuinely absent
 *     field is eligible for the legacy-worker fallback.
 *  2. If quote-time and build-time feeMeta are both present and valid but
 *     disagree, surface the disagreement (caller rejects the trade) — the
 *     worker's fee config changed between quote and build, so neither the
 *     quote's displayed minimum-received nor the fresh build can be trusted.
 *  3. If quote-time feeMeta is ABSENT but build-time feeMeta is present (a
 *     rolling worker deployment: an old/cached quote from a pre-feeMeta
 *     worker paired with a build from a newly-deployed worker that does send
 *     feeMeta), synthesize the expected quote-time policy from the caller's
 *     fallback contract (`fallback` + `fallbackFeeRecipient` - the same
 *     values the UI displayed for the fallback quote, see
 *     getKyberFeeFallback/KYBER_FEE_RECIPIENT_FALLBACK) and compare the
 *     build's feeMeta against that synthesized policy exactly as in (1).
 *     Without this, a build that silently disagrees with what was displayed
 *     would execute unnoticed. This is only sound because the fallback
 *     values are themselves believed to match the currently-deployed
 *     worker's real config (see getKyberFeeFallback's doc comment); it is
 *     not a substitute for feeMeta actually being sent everywhere.
 *  4. Otherwise prefer the quote's feeMeta (what was displayed to the user),
 *     then the build's feeMeta, then the caller-supplied hardcoded fallback
 *     (for workers that don't send feeMeta at all yet).
 */
export function resolveKyberFeeMeta(
  quoteFeeMetaRaw: unknown,
  buildFeeMetaRaw: unknown,
  fallback: ResolvedKyberFeeMeta,
  fallbackFeeRecipient: string,
): KyberFeeResolution {
  const quoteFeeMeta = parseKyberFeeMeta(quoteFeeMetaRaw)
  const buildFeeMeta = parseKyberFeeMeta(buildFeeMetaRaw)
  const invalid = {
    quote: quoteFeeMetaRaw !== undefined && quoteFeeMeta === undefined,
    build: buildFeeMetaRaw !== undefined && buildFeeMeta === undefined,
  }
  if (invalid.quote || invalid.build) {
    return { fee: fallback, invalid }
  }

  // Quote-time feeMeta is absent but a build sent feeMeta: synthesize the
  // policy the fallback contract promises (what the UI displayed) so the
  // build can still be checked for disagreement instead of being trusted
  // unconditionally. When quoteFeeMeta IS present this is unused (real
  // quote-time feeMeta always wins), and when buildFeeMeta is also absent
  // there's nothing to compare it against, so this only ever matters for
  // the quote-absent/build-present combination.
  const synthesizedQuoteMeta: KyberFeeMeta = {
    feeBps: fallback.feeBps,
    feeRecipient: fallbackFeeRecipient,
    quoteIsNetOfFee: fallback.quoteIsNetOfFee,
    chargeFeeBy: fallback.chargeFeeBy,
  }
  const effectiveQuoteMeta = quoteFeeMeta ?? (buildFeeMeta ? synthesizedQuoteMeta : undefined)

  if (effectiveQuoteMeta && buildFeeMeta && !kyberFeeMetaAgrees(effectiveQuoteMeta, buildFeeMeta)) {
    return { fee: fallback, disagreement: { quote: effectiveQuoteMeta, build: buildFeeMeta } }
  }

  const effective = quoteFeeMeta ?? buildFeeMeta
  if (!effective) {
    return { fee: fallback }
  }
  return {
    fee: { feeBps: effective.feeBps, quoteIsNetOfFee: effective.quoteIsNetOfFee, chargeFeeBy: effective.chargeFeeBy },
  }
}
